import React from 'react';
import './SettingsPage.css';
import { PageHeader } from '../../components/PageHeader/PageHeader';
import type { AppSettings, PageId, ConfirmConfig } from '../../types';

interface SettingsPageProps {
  settings: AppSettings;
  onUpdate: (settings: AppSettings) => void;
  onNavigate: (page: PageId) => void;
  onExport: () => void;
  onClearAll: () => void;
  showToast: (msg: string) => void;
  showConfirm: (config: ConfirmConfig) => void;
  onLogout?: () => void;
  username?: string;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const SettingsPage: React.FC<SettingsPageProps> = ({
  settings, onUpdate, onNavigate, onExport, onClearAll, showToast, showConfirm, onLogout, username,
}) => {
  const update = (partial: Partial<AppSettings>) => {
    onUpdate({ ...settings, ...partial });
    showToast('Settings saved');
  };

  const toggleDay = (index: number) => {
    const newDays = [...settings.activeDays];
    newDays[index] = !newDays[index];
    // Ensure at least one day stays active
    if (newDays.every((d) => !d)) return;
    update({ activeDays: newDays });
  };

  const handleClearAll = () => {
    showConfirm({
      title: 'Clear All Data',
      message: 'This will delete all task progress, calendar history, custom tasks, and schedule customizations. This cannot be undone.',
      confirmLabel: 'Clear Everything',
      danger: true,
      onConfirm: onClearAll,
    });
  };

  return (
    <div className="settings-page">
      <PageHeader title="Settings" />

      <div className="settings-content">
        {/* Household Name */}
        <section className="settings-section">
          <h3 className="settings-section-title">Household</h3>
          <input
            className="settings-input"
            placeholder="Household name (optional)"
            value={settings.householdName}
            onChange={(e) => update({ householdName: e.target.value })}
          />
        </section>

        {/* Active Days */}
        <section className="settings-section">
          <h3 className="settings-section-title">Active Days</h3>
          <p className="settings-hint">Choose which days appear in your schedule</p>
          <div className="settings-days">
            {DAY_NAMES.map((name, i) => (
              <button
                key={name}
                className={`settings-day-chip ${settings.activeDays[i] ? 'settings-day-chip--active' : ''}`}
                onClick={() => toggleDay(i)}
              >
                {name}
              </button>
            ))}
          </div>
        </section>

        {/* Auto Reset */}
        <section className="settings-section">
          <div className="settings-toggle-row">
            <div>
              <h3 className="settings-section-title" style={{ marginBottom: 0 }}>Auto Reset Weekly</h3>
              <p className="settings-hint">Automatically clear tasks when a new week starts</p>
            </div>
            <button
              className={`settings-switch ${settings.autoReset ? 'settings-switch--on' : ''}`}
              onClick={() => update({ autoReset: !settings.autoReset })}
              role="switch"
              aria-checked={settings.autoReset}
            >
              <span className="settings-switch-thumb" />
            </button>
          </div>
        </section>

        {/* Customize Schedule */}
        <section className="settings-section">
          <h3 className="settings-section-title">Schedule</h3>
          <button className="settings-action-btn" onClick={() => onNavigate('customize')}>
            <span>Customize Task List</span>
            <svg viewBox="0 0 24 24" width="16" height="16">
              <polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </section>

        {/* Data Management */}
        <section className="settings-section">
          <h3 className="settings-section-title">Data</h3>
          <button className="settings-action-btn" onClick={onExport}>
            <span>Export Data</span>
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="settings-action-btn settings-action-btn--danger" onClick={handleClearAll}>
            <span>Clear All Data</span>
            <svg viewBox="0 0 24 24" width="16" height="16">
              <polyline points="3 6 5 6 21 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </section>

        {/* Account */}
        {onLogout && (
          <section className="settings-section">
            <h3 className="settings-section-title">Account</h3>
            {username && <p className="settings-hint">Signed in as {username}</p>}
            <button className="settings-action-btn settings-action-btn--signout" onClick={onLogout}>
              <span>Sign Out</span>
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </section>
        )}

        <footer className="settings-footer">
          <p>Weekly Cleaning Schedule v1.0</p>
          <p>Built with React + Cloudflare Workers</p>
        </footer>
      </div>
    </div>
  );
};
